import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import * as crypto from 'node:crypto';
import { Exam } from '../exam.js';
import { aggregateOCRFromBoundingBoxes } from '../utils/exam-utils.js';
import {
  buildAdditionalInfo,
  buildExamTasksFromRecursiveStructure,
} from './agents/extraction-modules/build-exam-tasks.js';
import { createBoundingBoxWithOCR } from './agents/extraction-modules/ocr-utils.js';
// import { resolveNeedsAnswerForTask } from './agents/extraction-modules/needs-answer-agent.js';
import { OcrExtractionType } from './agents/extraction-modules/ocrExtractionTypes.js';
import { TaskType } from './task-types/task-main.js';

function collectPersistedDescriptionBoxesById(tasks: any[] | undefined, map: Map<string, any>) {
  if (!Array.isArray(tasks)) return;
  for (const task of tasks) {
    if (Array.isArray(task?.boundingBoxes)) {
      for (const box of task.boundingBoxes) {
        const boxId = String(box?._id ?? box?.id ?? '').trim();
        if (!boxId) continue;
        map.set(boxId, box);
      }
    }
    if (Array.isArray(task?.subTasks)) {
      collectPersistedDescriptionBoxesById(task.subTasks, map);
    }
  }
}

interface PersistedNeedsAnswerSnapshot {
  needsAnswer: boolean;
  description: string;
  masterSolutionContent: string;
  gradingCriteria: string;
  childSignatures: string[];
}

function normalizeComparableText(value: unknown): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getPersistedMasterSolutionContent(task: any): string {
  const doc = task?.masterSolution?.doc ?? task?.masterSolution;
  return normalizeComparableText(doc?.content);
}

function getPersistedGradingCriteria(task: any): string {
  const doc = task?.gradingScheme?.doc ?? task?.gradingScheme;
  return normalizeComparableText(doc?.criteria);
}

function buildChildSignaturesFromPersistedTask(task: any): string[] {
  const children = Array.isArray(task?.subTasks) ? task.subTasks : [];
  return children
    .map((child: any) => {
      const taskNumber = normalizeComparableText(child?.taskNumber);
      const description = normalizeComparableText(child?.description);
      return `${taskNumber}::${description}`;
    })
    .sort();
}

/*
// Helpers used by the needsAnswer agent call — restore when re-enabling:
function getNodeMasterSolutionContent(task: any): string {
  return normalizeComparableText(task?.masterSolutionContent);
}

function getNodeGradingCriteria(task: any): string {
  return normalizeComparableText(task?.gradingCriteria);
}

function getNodeDescription(task: any): string {
  const boxes = Array.isArray(task?.descriptionBoundingBoxes) ? task.descriptionBoundingBoxes : [];
  const described = normalizeComparableText(task?.description);
  return described || normalizeComparableText(aggregateOCRFromBoundingBoxes(boxes));
}

function buildChildSignaturesFromRecursiveNode(task: any): string[] {
  const children = Array.isArray(task?.subtasks) ? task.subtasks : [];
  return children
    .map((child: any) => {
      const taskNumber = normalizeComparableText(child?.taskNumber);
      const description = getNodeDescription(child);
      return `${taskNumber}::${description}`;
    })
    .sort();
}

function areSortedArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

function hasNeedsAnswerRelevantChange(node: any, persisted?: PersistedNeedsAnswerSnapshot): boolean {
  if (!persisted) return true;
  const descriptionChanged = persisted.description !== getNodeDescription(node);
  const masterSolutionChanged = persisted.masterSolutionContent !== getNodeMasterSolutionContent(node);
  const gradingCriteriaChanged = persisted.gradingCriteria !== getNodeGradingCriteria(node);
  const childSignaturesChanged = !areSortedArraysEqual(
    persisted.childSignatures,
    buildChildSignaturesFromRecursiveNode(node),
  );
  return descriptionChanged || masterSolutionChanged || gradingCriteriaChanged || childSignaturesChanged;
}
*/

function collectPersistedNeedsAnswerByPath(
  tasks: any[] | undefined,
  parentPath: string[],
  map: Map<string, PersistedNeedsAnswerSnapshot>,
) {
  if (!Array.isArray(tasks)) return;

  for (const task of tasks) {
    const taskNumber = normalizeComparableText(task?.taskNumber);
    if (!taskNumber) continue;

    const taskPath = [...parentPath, taskNumber];
    const key = taskPath.join('|');
    map.set(key, {
      needsAnswer: task?.needsAnswer !== false,
      description: normalizeComparableText(task?.description),
      masterSolutionContent: getPersistedMasterSolutionContent(task),
      gradingCriteria: getPersistedGradingCriteria(task),
      childSignatures: buildChildSignaturesFromPersistedTask(task),
    });

    if (Array.isArray(task?.subTasks)) {
      collectPersistedNeedsAnswerByPath(task.subTasks, taskPath, map);
    }
  }
}

async function assignNeedsAnswerRecursively(
  tasks: any[] | undefined,
  parentPath: string[],
  persistedNeedsAnswerByPath: Map<string, PersistedNeedsAnswerSnapshot>,
) {
  if (!Array.isArray(tasks)) return;

  for (const node of tasks) {
    const taskNumber = normalizeComparableText(node?.taskNumber);
    if (!taskNumber) continue;
    const taskPath = [...parentPath, taskNumber];
    const children = Array.isArray(node?.subtasks) ? node.subtasks : [];
    // needsAnswer: simplified to TASK_FRAME-only check while testing (agent kept below, commented out)
    node.needsAnswer = (node?.taskType ?? TaskType.DEFAULT) !== TaskType.TASK_FRAME;

    /*
    // Original needsAnswer agent logic — re-enable to restore LLM-based resolution:
    const persisted = persistedNeedsAnswerByPath.get(taskPath.join('|'));
    if (children.length === 0) {
      node.needsAnswer = true;
    } else if (!hasNeedsAnswerRelevantChange(node, persisted)) {
      node.needsAnswer = persisted!.needsAnswer;
    } else {
      const resolution = await resolveNeedsAnswerForTask({
        taskNumber: taskPath.join('|'),
        taskDescription: getNodeDescription(node),
        subtasks: children.map((child: any) => ({
          subTaskNumber: normalizeComparableText(child?.taskNumber),
          description: getNodeDescription(child),
        })),
        masterSolutionContent: getNodeMasterSolutionContent(node),
        gradingCriteria: getNodeGradingCriteria(node),
      });
      node.needsAnswer = resolution.needsAnswer;
    }
    */

    await assignNeedsAnswerRecursively(children, taskPath, persistedNeedsAnswerByPath);
  }
}

export const createExamTasksMutation = registerMutation({
  operationName: 'createExamTasksMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'tasksJson', type: () => String }, // JSON string of recursive tasks
    { name: 'bbxOutput', type: () => String }, // Optional: JSON string with regions and additionalInformation
    { name: 'imageIds', type: () => [String] }, // Image IDs for OCR
    { name: 'rerunDescriptionOCRBoxIds', type: () => [String] },
  ],

  resolve:
    (
      examId: string,
      tasksJson: string,
      bbxOutput?: string,
      imageIds?: string[],
      rerunDescriptionOCRBoxIds: string[] = [],
    ) =>
    async () => {
      try {
        const examCollection = await getCollection(Exam as any);
        const existingExam = await examCollection.findOne({ _id: examId } as any, { projection: { tasks: 1 } });
        const imageArray = imageIds || [];
        const rerunDescriptionOCRBoxIdSet = new Set((rerunDescriptionOCRBoxIds || []).map(boxId => String(boxId)));
        const persistedBoxesById = new Map<string, any>();
        const persistedNeedsAnswerByPath = new Map<string, PersistedNeedsAnswerSnapshot>();
        collectPersistedDescriptionBoxesById(existingExam?.tasks as any[] | undefined, persistedBoxesById);
        collectPersistedNeedsAnswerByPath(existingExam?.tasks as any[] | undefined, [], persistedNeedsAnswerByPath);

        // Parse enriched recursive tasks
        let parsed;
        try {
          parsed = JSON.parse(tasksJson);
        } catch (e) {
          throw new Error('Invalid tasks JSON');
        }
        if (!parsed.tasks || !Array.isArray(parsed.tasks)) throw new Error('tasks must be an array in the root object');

        // Guard against stale client state overwriting OCR text with empty strings.
        // If an incoming box has empty OCR text but the same persisted box already has text,
        // keep the persisted OCR values.
        let preservedFromPersistedCount = 0;
        parsed.tasks = parsed.tasks.map((task: any) => {
          const incomingBoxes = Array.isArray(task?.descriptionBoundingBoxes) ? task.descriptionBoundingBoxes : [];
          if (incomingBoxes.length === 0) return task;

          const mergedBoxes = incomingBoxes.map((box: any) => {
            const boxId = String(box?._id ?? box?.id ?? '').trim();
            if (!boxId) return box;
            const incomingText = String(box?.ocrExtractedText ?? '').trim();
            if (incomingText) return box;

            const persisted = persistedBoxesById.get(boxId);
            const persistedText = String(persisted?.ocrExtractedText ?? '').trim();
            if (!persistedText) return box;

            preservedFromPersistedCount += 1;
            return {
              ...box,
              ocrExtractedText: persisted.ocrExtractedText,
              confidence: box?.confidence ?? persisted?.confidence,
              imageId: box?.imageId ?? persisted?.imageId ?? null,
            };
          });

          return {
            ...task,
            descriptionBoundingBoxes: mergedBoxes,
            description: aggregateOCRFromBoundingBoxes(mergedBoxes),
          };
        });

        if (rerunDescriptionOCRBoxIdSet.size > 0 && imageArray.length > 0) {
          let rerunMatchedBoxes = 0;
          parsed.tasks = await Promise.all(
            parsed.tasks.map(async (task: any) => {
              const existingBoxes = Array.isArray(task?.descriptionBoundingBoxes) ? task.descriptionBoundingBoxes : [];
              if (existingBoxes.length === 0) return task;

              const updatedBoxes = await Promise.all(
                existingBoxes.map(async (box: any) => {
                  const boxId = String(box?._id ?? box?.id ?? '');
                  if (!rerunDescriptionOCRBoxIdSet.has(boxId)) return box;
                  if (box?.approved === true) return box;
                  rerunMatchedBoxes += 1;

                  const topLeft = box?.topLeft;
                  const bottomRight = box?.bottomRight;
                  const hasCoordinates =
                    typeof topLeft?.x === 'number' &&
                    typeof topLeft?.y === 'number' &&
                    typeof bottomRight?.x === 'number' &&
                    typeof bottomRight?.y === 'number';

                  if (!hasCoordinates) return box;

                  try {
                    return await createBoundingBoxWithOCR(
                      {
                        boundingBoxId: boxId || crypto.randomUUID(),
                        pageNumber: Math.max(1, Number(box?.pageNumber ?? 1)),
                        confidence: typeof box?.confidence === 'number' ? box.confidence : undefined,
                        bbox: {
                          x1: topLeft.x,
                          y1: topLeft.y,
                          x2: bottomRight.x,
                          y2: bottomRight.y,
                        },
                      },
                      imageArray,
                      OcrExtractionType.TASK_DESCRIPTION,
                      task?.taskType || TaskType.DEFAULT,
                    );
                  } catch (error) {
                    // Don't let one box's OCR failure abort the whole structure save (which would
                    // silently drop the task/box being saved along with it, since this rerun runs
                    // inline with the full-structure write). Keep the box as-is and let the
                    // teacher retry OCR for it explicitly afterwards.
                    logger.error({ err: error, boxId, examId }, 'OCR failed for box during structure save, keeping box unchanged');
                    return box;
                  }
                }),
              );

              return {
                ...task,
                descriptionBoundingBoxes: updatedBoxes,
                description: aggregateOCRFromBoundingBoxes(updatedBoxes),
              };
            }),
          );
        }

        // Parse BBX output for additional information
        let bbxData: any = { additionalInformation: [], tasks: [] };
        if (bbxOutput) {
          try {
            bbxData = JSON.parse(bbxOutput);
          } catch (e) {
            logger.warn({ examId }, 'Invalid BBX output JSON, skipping additional information');
          }
        }

        // Build AdditionalInfo objects
        const additionalInfoList = await buildAdditionalInfo(bbxData, imageArray);

        // On structure-editor saves, keep existing confidence and stamp new/approved boxes as 1.
        parsed.tasks = parsed.tasks.map((task: any) => ({
          ...task,
          descriptionBoundingBoxes: (Array.isArray(task?.descriptionBoundingBoxes)
            ? task.descriptionBoundingBoxes
            : []
          ).map((box: any) => {
            const boxId = String(box?._id ?? box?.id ?? '').trim();
            if (!boxId || !persistedBoxesById.has(boxId) || box?.approved === true || box?.confidence == null) {
              // `box` may be a real BoundingBox entity here (e.g. one just produced by the OCR-rerun
              // step above) — spreading an entity instance directly copies its internal upgrade
              // marker onto a plain object, which later tricks the entity constructor into treating
              // that plain object as already-constructed and skipping re-wrapping (losing .toObject()
              // and the rest of the entity's methods). Go through .toObject() first so the spread
              // always starts from plain data.
              const plainBox = typeof box?.toObject === 'function' ? box.toObject() : box;
              return { ...plainBox, confidence: 1 };
            }
            return box;
          }),
        }));

        await assignNeedsAnswerRecursively(parsed.tasks, [], persistedNeedsAnswerByPath);

        // Build exam tasks from enriched recursive structure
        const examTasks = await buildExamTasksFromRecursiveStructure(parsed.tasks, examId, undefined);

        // Update exam in database
        // Only update additionalInformation if bbxOutput was actually provided (not empty)
        const updateSet: any = {
          tasks: examTasks.map(t => t.toObject()),
        };
        if (bbxOutput?.trim()) {
          updateSet.additionalInformation = additionalInfoList.map(a => a.toObject());
        }

        await examCollection.updateOne({ _id: examId } as any, {
          $set: updateSet,
        });

        return true;
      } catch (err) {
        logger.error({ err, examId }, 'createExamTaskMutation error');
        throw err;
      }
    },
});
